import * as React from "react";
import { Select as BaseSelect } from "@base-ui/react/select";
import { cn } from "./cn";

export interface SelectOption {
    value: string;
    label: React.ReactNode;
    disabled?: boolean;
    /** Draw a divider above this option: statuses above, actions below. */
    separatorBefore?: boolean;
}

const triggerClasses =
    "inline-flex h-9 w-full items-center justify-between gap-2 rounded-md border border-border " +
    "bg-surface px-3 text-sm text-fg outline-none cursor-pointer " +
    "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent " +
    "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed";

const popupClasses =
    "max-h-[min(20rem,var(--available-height))] min-w-[var(--anchor-width)] " +
    "overflow-y-auto rounded-md border border-border bg-surface p-1 shadow-lg outline-none";

const itemClasses =
    "flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1.5 text-sm text-fg outline-none " +
    "data-[highlighted]:bg-accent data-[highlighted]:text-accent-fg " +
    "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed";

/**
 * Themed select (Base UI) — a styled alternative to native <select> for short,
 * known option lists. For large searchable lists use Combobox instead.
 *
 *   <Select.Root value={v} onValueChange={setV}>
 *     <Select.Trigger>
 *       <Select.Value placeholder="Pick one" />
 *       <Select.Icon>▾</Select.Icon>
 *     </Select.Trigger>
 *     <Select.Content>
 *       <Select.Item value="a">Option A</Select.Item>
 *     </Select.Content>
 *   </Select.Root>
 */
export const Select = {
    Root: BaseSelect.Root,
    Value: BaseSelect.Value,
    Icon: function Icon({
        className,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseSelect.Icon>) {
        return (
            <BaseSelect.Icon
                className={cn("text-muted", className as string)}
                {...props}
            />
        );
    },
    Trigger: function Trigger({
        className,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseSelect.Trigger>) {
        return (
            <BaseSelect.Trigger
                className={cn(triggerClasses, className as string)}
                {...props}
            />
        );
    },
    Item: function Item({
        className,
        children,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseSelect.Item>) {
        return (
            <BaseSelect.Item className={cn(itemClasses, className as string)} {...props}>
                <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
                <BaseSelect.ItemIndicator>✓</BaseSelect.ItemIndicator>
            </BaseSelect.Item>
        );
    },
    /** Portal + positioner + popup in one convenient wrapper. */
    Content: function Content({
        className,
        children,
        sideOffset = 4,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseSelect.Positioner>) {
        return (
            <BaseSelect.Portal>
                {/* z-index on the Positioner (the positioned element), not the Popup. */}
                <BaseSelect.Positioner className="z-[1100]" sideOffset={sideOffset} {...props}>
                    <BaseSelect.Popup className={cn(popupClasses, className as string)}>
                        {children}
                    </BaseSelect.Popup>
                </BaseSelect.Positioner>
            </BaseSelect.Portal>
        );
    },
};

export interface SelectBoxProps {
    value: string;
    onValueChange: (value: string) => void;
    options: SelectOption[];
    /** Shown when the value matches no option (rare — selects are controlled). */
    placeholder?: React.ReactNode;
    /** Forwarded to the trigger. Pass e.g. "w-auto" for inline filter selects. */
    className?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
    "aria-label"?: string;
    /** A small mark before the value inside the trigger (e.g. where the value came from). Not clickable: the trigger opens the list. */
    lead?: React.ReactNode;
}

/**
 * Drop-in replacement for a native <select> for short, known option lists.
 * One-line swap: pass `value`, `onValueChange`, and `options` ({ value, label }).
 * For large searchable lists use Combobox instead.
 *
 *   <SelectBox value={stage} onValueChange={setStage}
 *     options={STAGES.map(s => ({ value: s.key, label: s.label }))} />
 */
export function SelectBox({
    value,
    onValueChange,
    options,
    placeholder,
    className,
    disabled,
    "aria-label": ariaLabel,
    lead,
}: SelectBoxProps) {
    return (
        <BaseSelect.Root
            items={options}
            value={value}
            onValueChange={(v) => onValueChange((v ?? "") as string)}
            disabled={disabled}
        >
            <Select.Trigger className={className} aria-label={ariaLabel}>
                {/* One line always: a long label ends in an ellipsis rather than wrapping the trigger. */}
                <span className="flex min-w-0 items-center gap-1">
                    {lead && <span className="flex shrink-0 items-center">{lead}</span>}
                    <Select.Value className="min-w-0 truncate">
                        {(v: string) => options.find((o) => o.value === v)?.label ?? placeholder ?? null}
                    </Select.Value>
                </span>
                <Select.Icon>▾</Select.Icon>
            </Select.Trigger>
            <Select.Content>
                {options.map((o) => (
                    <React.Fragment key={o.value}>
                        {o.separatorBefore && <div role="separator" className="my-1 h-px bg-border" />}
                        <Select.Item value={o.value} disabled={o.disabled}>
                            {o.label}
                        </Select.Item>
                    </React.Fragment>
                ))}
            </Select.Content>
        </BaseSelect.Root>
    );
}
